import React, {useEffect, useRef, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {InputText} from "primereact/inputtext";
import {Toast} from "primereact/toast";
import {
    InputChangeEvent,
    IScheduledTaskPropertyRef
} from "components/pages/scheduled-task-airflow/components/InputFields";
import {DatasetCategoryDropdown} from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import {useGetDatasetCategories} from "api/api-hooks/apiHooks";
import {IPair} from "models/dataset-models";

interface ITaskConfigCategoryTableProps extends IScheduledTaskPropertyRef {
    header: string;
    categories: Record<string, number>
    onChange: (e: InputChangeEvent<Record<string, number>>) => void;
    bodyClass?: string;
}

interface ICategoryRowModel {
    key: string,
    value: number | undefined,
    newKey: string,
    newValue: number | undefined,
    editMode: boolean,
}

export default function TaskConfigCategoryTable(props: ITaskConfigCategoryTableProps) {
    const toast = useRef<Toast>(null);
    const [dataArr, setDataArr] = useState<ICategoryRowModel[]>(mapToList(props.categories));
    const [titleMap, setTitleMap] = useState<Record<number, string>>({});

    const categoryQuery = useGetDatasetCategories();

    useEffect(() => {
        const titleMap: Record<number, string> = {}
        if(categoryQuery.data) {
            categoryQuery.data.forEach((it) => {
                titleMap[it.id!!] = it.toString();
            });

            setTitleMap(titleMap)
        }
    }, [categoryQuery.data, dataArr])

    const propagateChangesStateUp = (arr: IPair[]) => {
        const c: Record<string, number> = {}

        arr.forEach((e) => {
            if(e.value) c[e.key] = e.value; // Category id must be defined
        })

        props.onChange({value: c, property: props.property});
    }

    const confirmWrapper = (event: any, confirmHandler: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you really want to delete this category mapping?',
            icon: 'pi pi-exclamation-triangle',
            accept: confirmHandler,
        });
    }

    const showWarn = () => {
        toast.current?.show({severity:'warn', summary: 'Invalid', detail:'Category must be selected!', life: 3000});
    }

    const handleEdit = (row: ICategoryRowModel) => {
        const idx = dataArr.indexOf(row)

        dataArr[idx].editMode = true
        dataArr[idx].newValue = dataArr[idx].value
        dataArr[idx].newKey = dataArr[idx].key
        setDataArr([...dataArr])
    }

    const handleEditCancel = (row: ICategoryRowModel) => {
        const idx = dataArr.indexOf(row)

        // Check if item is selected, if not remove row
        if(row.newValue == null || row.value == null) {
            setDataArr([...dataArr.filter(item => item.key !== row.key)]);
            return
        }

        dataArr[idx].editMode = false
        dataArr[idx].newValue = dataArr[idx].value
        dataArr[idx].newKey = dataArr[idx].key
        setDataArr([...dataArr])
    }

    const handleEditConfirm = (row: ICategoryRowModel) => {
        const idx = dataArr.indexOf(row)

        if(dataArr[idx].newValue === undefined) {
            showWarn()
            return
        }

        dataArr[idx].editMode = false
        dataArr[idx].value = dataArr[idx].newValue
        dataArr[idx].key = dataArr[idx].newKey

        const updated = [...dataArr]
        setDataArr(updated)
        propagateChangesStateUp(updated)
    }

    const handleAdd = () => {
        const updated = [...dataArr, { newKey: "new_category", newValue: undefined, editMode: true} as ICategoryRowModel]
        setDataArr(updated);
    }

    const handleDelete = (row: ICategoryRowModel) => {
        const filtered = [...dataArr.filter(item => item.key !== row.key)]
        setDataArr(filtered);
        propagateChangesStateUp(filtered)
    }

    const handleValueChange = (e: any, row: ICategoryRowModel) => {
        const idx = dataArr.indexOf(row)
        dataArr[idx].newValue = e
        setDataArr([...dataArr])
    }

    const handleKeyChange = (e: any, row: ICategoryRowModel) => {
        const idx = dataArr.indexOf(row)
        dataArr[idx].newKey = e.target.value
        setDataArr([...dataArr])
    }

    const headerTemplate = (
        <div className="flex justify-content-between">
            <h4>{props.header}</h4>
            <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()}/>
        </div>
    );

    const keyBodyTemplate = (row: ICategoryRowModel) => {
        return <>
            { !row.editMode &&
                <div>
                    {row.key}
                </div>
            }

            { row.editMode &&
                <InputText placeholder="Key" value={row.newKey || ''}  onChange={ e => handleKeyChange(e, row) }/>
            }
        </>
    }

    const valueBodyTemplate = (row: ICategoryRowModel) => {
        return <>
            { !row.editMode &&
                <div>
                    {titleMap[row.value!!]}
                </div>
            }

            { row.editMode &&
                <DatasetCategoryDropdown value={row.newValue} onChange={ e => handleValueChange(e, row) }/>
            }
        </>
    }


    const actionBodyTemplate = (row: ICategoryRowModel) => {
        return <>
            { !row.editMode &&
                <Button icon="pi pi-pencil" className="p-button-info p-button-rounded p-button-text" onClick={(e) => handleEdit(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-check" className="p-button-info p-button-rounded p-button-text" onClick={(e) => handleEditConfirm(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-times" className="p-button-plain p-button-rounded p-button-text" onClick={(e) => handleEditCancel(row)} />
            }

            <Button icon="pi pi-trash" className="const-del-btn  p-button-rounded p-button-text p-button-danger" onClick={(e) => confirmWrapper(e, () => handleDelete(row))} />
        </>
    }


    return <>
        <Toast ref={toast}></Toast>

        <DataTable value={dataArr}
                   responsiveLayout="scroll"
                   header={headerTemplate}
                   width={"100%"}
                   className={props.bodyClass}
                   size="small"
        >
            <Column header="Key" body={keyBodyTemplate} style={{width: "20rem"}}/>
            <Column header="Value" body={valueBodyTemplate}/>
            <Column body={actionBodyTemplate} bodyStyle={{textAlign: "right"}}/>
        </DataTable>

        <ConfirmPopup />
    </>
}

function mapToList(map: Record<string, number>): ICategoryRowModel[] {
    return Object.keys(map).map((k) => {
        return { key: k, value: map[k], editMode: false} as ICategoryRowModel;
    })
}